import React, {useCallback, useMemo} from 'react';
import {Box, Card, Typography} from '@mui/material';
import {formatMoney, htmlToString} from '../../../../core/services/utils/utils';
import useAccessLevels from '../../../hooks/use-access-levels';
import {StandaloneCheckbox} from '../../../components/base/checkbox/checkbox';
import QuantityStepper from './quantity-stepper';
import {ProductTitleText} from '../../../views/panel/operator/products/single';

const DENSITY_STYLES = {
    medium: {
        contentPx: 2,
        contentPt: 1.5,
        titleVariant: 'subtitle2',
        descriptionVariant: 'caption',
        stepperPy: 1,
    },
    compact: {
        contentPx: 2,
        contentPt: 1.5,
        titleVariant: 'subtitle2',
        descriptionVariant: 'caption',
        stepperPy: 1,
    },
};

const SuggestiveSellingCompactCard = ({
                                          part,
                                          selected,
                                          quantity,
                                          onQuantityChange,
                                          onSelectionChange,
                                          disabled,
                                          minHeight = 132,
                                          descriptionLineClamp = 3,
                                          density = 'compact',
                                      }) => {
    const operatorAccessLevels = useAccessLevels();

    const priceLabel = useMemo(() => {
        if (!operatorAccessLevels.packages.sales.pricesInLookup || part.priceInfo?.newPrice == null) {
            return null;
        }
        return formatMoney(part.priceInfo.newPrice);
    }, [operatorAccessLevels.packages.sales.pricesInLookup, part.priceInfo?.newPrice]);

    const handleQuantityChange = useCallback((next) => {
        onQuantityChange(part.partId, next);
    }, [onQuantityChange, part.partId]);

    const handleSelectionChange = useCallback((_, checked) => {
        onSelectionChange(part.partId, checked);
    }, [onSelectionChange, part.partId]);

    const densityStyles = DENSITY_STYLES[density] ?? DENSITY_STYLES.compact;

    return (
        <Card
            variant={'outlined'}
            sx={{
                position: 'relative',
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                borderColor: selected ? 'primary.main' : 'divider',
                borderWidth: selected ? 2 : 1,
                boxSizing: 'border-box',
                minHeight,
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: 4,
                    left: 4,
                    zIndex: 1,
                    lineHeight: 0,
                }}
            >
                <StandaloneCheckbox
                    checked={selected}
                    disabled={disabled}
                    onChange={handleSelectionChange}
                />
            </Box>
            <Box
                sx={{
                    px: densityStyles.contentPx,
                    pt: densityStyles.contentPt,
                    pb: 0.5,
                    pl: densityStyles.contentPx + 3,
                    flex: 1,
                    minWidth: 0,
                }}
            >
                <ProductTitleText
                    partNo={part.partNumber}
                    title={part.title ?? ''}
                    partNoClassName={'text-align-start'}
                    mainTextVariant={densityStyles.titleVariant}
                    secondaryTextVariant={'caption'}
                />
                <Typography
                    variant={densityStyles.descriptionVariant}
                    color={'text.secondary'}
                    component={'div'}
                    sx={{
                        mt: 0.5,
                        display: '-webkit-box',
                        WebkitLineClamp: descriptionLineClamp,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.3,
                    }}
                >
                    {htmlToString(part.description ?? '', 200)}
                </Typography>
                {
                    priceLabel &&
                    <Typography variant={'caption'} fontWeight={600} color={'primary.main'} sx={{mt: 0.75}}>
                        {priceLabel}
                    </Typography>
                }
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    width: '100%',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: densityStyles.stepperPy,
                    borderTop: 1,
                    borderColor: 'divider',
                }}
            >
                <QuantityStepper
                    value={quantity ?? 0}
                    onChange={handleQuantityChange}
                    disabled={disabled}
                    min={operatorAccessLevels.packages.sales.issueCredit ? undefined : 0}
                />
            </Box>
        </Card>
    );
};

export default SuggestiveSellingCompactCard;
